import { Helmet } from "react-helmet-async";
import { useTranslation } from "react-i18next";
import React, { useState, useEffect, useCallback, useMemo, useRef } from "react";
import Layout from '../../Layout';
import CreateCustomerModal from "../../components/Customer/CreateCustomerModal";
import LeadDrawer, { type LeadAction } from "./lead/LeadDrawer";
import BranchBoard from "./lead/BranchBoard";
import { Link, useSearchParams } from "react-router-dom";
import { parseFilters, buildFilterParams, saveCustomerListQuery, CustomerListFilters } from "./filterState";
import { ChannelCustomerItem, deleteCustomerApi } from "../../api/ChannelCustomer";
import { getCustomerTags, type CustomerTagItem } from "../../api/CustomerTag";
import { extractApiError } from "../../lib/apiError";
import useAuthStore from "../../store/authStore";
import { mixGetV1 } from "../../services/mixin";
import { useDebounce } from "../../hooks/useDebounce";
import { Table, Button, Modal, Dropdown, message, Switch, Tooltip, Empty, Segmented, Select } from "antd";
import { ExclamationCircleFilled } from "@ant-design/icons";
import {
    AlarmClock, Bot, CalendarCheck2, CalendarPlus, ClipboardEdit, Eye, MessageCircle, MoreHorizontal, Phone, PhoneMissed,
    ChevronDown, Headset, PhoneOutgoing, Plus, Shuffle, SlidersHorizontal, Sparkles, Trash2, TriangleAlert, UserPlus, UserRoundX, Users,
} from "lucide-react";
import Toolbar from "components/Customer/ToolbarTelesales";
import StatusTabs from "components/Customer/StatusTabsTelesales";
import useTableColumnSettings, { SettingColumn } from "hooks/useTableColumnSettings";
import { ColumnSettingButton } from "components/TableColumnSetting";
import dayjs from "dayjs";
import {
    activeAppt, chatTagsOf, dueText, inLeadList, lastTouchOf, textOn, isLeadTag, mangOf, maskPhone, nextStep, progressOf, shortTag, sourceOf, staffOf, tagOf, when,
    type LeadLocal, type NextStep,
} from "./lead/logic";
import { useLeadStore } from "./lead/store";
import { onCustomerPatched } from "./lead/api";
import { buildDemo, enrichDemo, isDemoId, type DemoCustomer, type Staff } from "./lead/demo";
import { fetchCareStatuses, type CareStatusItem } from "../RoomChat/chatLabels";
import ReassignSettings from "./lead/ReassignSettings";
import { ChatTagPicker, CsStatusPicker } from "./lead/TagPicker";
import ReassignModal, { type ReassignPick } from "./lead/ReassignModal";
import {
    chatIdleInfo, chatStaffOf, findChatStale, findStale, idleInfo, idleText, planReassign, thresholdText,
    type ChatReassignCfg, type ReassignCfg, type StaleLead,
} from "./lead/reassign";
import { groupOfLead, useGroupCfg } from "./lead/groups";
import { ROLES, ROLE_META, currentMe, type Role } from "./lead/roles";
import type { Tone } from "./dashboard/ui";

const SELECTION_COL_WIDTH = 48;
/** Theo dõi media query (vd: độ rộng màn hình) */
function useMedia(query: string) {
    const [match, setMatch] = useState(() => window.matchMedia(query).matches);
    useEffect(() => {
        const mq = window.matchMedia(query);
        const h = () => setMatch(mq.matches);
        mq.addEventListener("change", h);
        return () => mq.removeEventListener("change", h);
    }, [query]);
    return match;
}

/** Nhớ ngăn hồ sơ đang ở chế độ mở rộng */
const DRAWER_WIDE_KEY = "tele_lead_drawer_wide";
/** Độ rộng vùng danh sách tối thiểu để 6 ô lọc nằm vừa một hàng */
const FILTERS_ROW_MIN = 1270;
/** Nhớ trạng thái mở / thu bộ lọc trên máy (màn hình nhỏ) */
const FILTERS_OPEN_KEY = "tele_lead_filters_open";
/** Vùng danh sách hẹp hơn mức này thì bảng chuyển sang chế độ gọn (không cuộn ngang) */
const COMPACT_TABLE_MAX = 1100;
/** Cột theo người dùng */
const ROLE_COLUMNS: Record<Exclude<Role, "branch">, string[]> = {
    ops: ["customerName", "phone", "csCode", "deadline", "staff", "serviceCategory", "chatTags", "source", "pageStaff", "createdAt", "lastTouch", "branch", "actions"],
    tele: ["customerName", "phone", "csCode", "deadline", "serviceCategory", "chatTags", "source", "pageStaff", "lastTouch", "branch", "createdAt", "actions"],
    page: ["customerName", "phone", "csStatus", "chatTags", "progress", "staff", "source", "pageStaff", "createdAt", "actions"],
};

/** Màu biểu tượng ô số liệu */
const PILL_TONE: Record<Tone, string> = {
    teal: "bg-[#e8f4f7] text-[#2c6b7f] dark:bg-[#3e91ab]/20 dark:text-[#7cc3d6]",
    red: "bg-red-50 text-red-600 dark:bg-red-500/15 dark:text-red-400",
    amber: "bg-amber-50 text-amber-600 dark:bg-amber-500/15 dark:text-amber-400",
    green: "bg-green-50 text-green-600 dark:bg-green-500/15 dark:text-green-400",
    indigo: "bg-indigo-50 text-indigo-600 dark:bg-indigo-500/15 dark:text-indigo-400",
    sky: "bg-sky-50 text-sky-600 dark:bg-sky-500/15 dark:text-sky-400",
    slate: "bg-gray-100 text-gray-600 dark:bg-gray-700 dark:text-gray-300",
};
/** Vùng cuộn của Layout — dùng cho tiêu đề bảng dính */
const scrollBox = () => (document.querySelector("[class*=Layout_mainContent]") as HTMLElement | null) ?? window;

const Muted = ({ children }: { children: React.ReactNode }) => <span className="text-gray-400">{children}</span>;
const DepositMark = () => <Tooltip title="Khách đã có khoản cọc được xác nhận"><span className="shrink-0 rounded bg-emerald-100 px-1 text-[10px] font-semibold text-emerald-700 dark:bg-emerald-500/20 dark:text-emerald-300">Cọc</span></Tooltip>;

/* ---------- Ô số liệu (lọc nhanh) theo người dùng ---------- */

interface QuickDef { key: string; label: string; icon: React.ReactNode; tone: Tone; hint: string; test: (r: ChannelCustomerItem, l: LeadLocal | undefined, s: NextStep) => boolean }

const isLate = (s: NextStep, now: number) => !!s.due && s.due < now && s.kind !== "appt" && s.kind !== "none";
const isToday = (t: number | undefined, now: number) => !!t && dayjs(t).isSame(now, "day");

const quickDefs = (role: Role, now: number): QuickDef[] => {
    const late: QuickDef = { key: "late", label: "Quá hạn xử lý", icon: <AlarmClock size={15} />, tone: "red", hint: "Khách mới quá 15 phút chưa gọi, hẹn gọi lại / gọi lại / xác nhận lịch đã quá hạn", test: (_r, _l, s) => isLate(s, now) };
    const fresh: QuickDef = { key: "new", label: "Khách mới chưa gọi", icon: <Sparkles size={15} />, tone: "teal", hint: "Mã Mới / S0, chưa có cuộc gọi đi nào", test: (_r, _l, s) => s.kind === "new" };
    const recall: QuickDef = { key: "recall", label: "Gọi lại hôm nay", icon: <PhoneOutgoing size={15} />, tone: "amber", hint: "Cuộc gọi nhỡ, hẹn gọi lại hoặc gọi lại lần 2–3 sau khi không liên lạc được, hạn trong hôm nay", test: (_r, _l, s) => (s.kind === "missed" || s.kind === "callback" || s.kind === "retry") && !!s.due && s.due < dayjs(now).endOf("day").valueOf() };
    const confirm: QuickDef = { key: "confirm", label: "Lịch cần xác nhận", icon: <CalendarCheck2 size={15} />, tone: "sky", hint: "Lịch hẹn khách chưa xác nhận — lịch sáng xác nhận trước 18:00 hôm trước, lịch chiều trước 2 giờ", test: (_r, _l, s) => s.kind === "confirm" };
    if (role === "page") {
        return [
            { key: "mine_today", label: "Tạo hôm nay", icon: <UserPlus size={15} />, tone: "teal", hint: "Khách tạo trong hôm nay", test: (r) => isToday(r.created_at ? dayjs(r.created_at).valueOf() : undefined, now) },
            { key: "uncalled", label: "Telesales chưa gọi", icon: <PhoneMissed size={15} />, tone: "red", hint: "Khách chưa có cuộc gọi nào — nhắc Telesales nếu quá 15 phút", test: (r, l) => progressOf(r, l, now).text.startsWith("Chưa gọi") },
            { key: "unreach", label: "Chưa liên lạc được", icon: <UserRoundX size={15} />, tone: "amber", hint: "Đã gọi nhưng chưa nghe máy / thuê bao — có thể nhắn thêm qua chat", test: (r, l) => progressOf(r, l, now).text.includes("chưa liên lạc") },
            { key: "booked", label: "Đã đặt lịch", icon: <CalendarCheck2 size={15} />, tone: "green", hint: "Khách đã có lịch hẹn hoặc đã đến cơ sở", test: (r, l) => /lịch|đến/i.test(progressOf(r, l, now).text) && !progressOf(r, l, now).text.startsWith("Không") },
        ];
    }
    if (role === "tele") return [late, fresh, recall, confirm];
    return [late, fresh, recall, confirm, { key: "unassigned", label: "Chưa phân Telesales", icon: <Users size={15} />, tone: "slate", hint: "Khách chưa có người Telesales phụ trách", test: (r, l) => !staffOf(r, l) }];
};

const CustomerCare: React.FC = () => {
    const { t, i18n } = useTranslation();
    const { isSuperAdmin, hasPermission } = useAuthStore();
    const canDelete = isSuperAdmin || hasPermission("customer.delete");
    const canManage = isSuperAdmin || hasPermission("telesales.assign") || hasPermission("telesales.config");
    const me = useMemo(currentMe, []);
    const storedView = useLeadStore((s) => s.view);
    const setView = useLeadStore((s) => s.setView);
    const assign = useLeadStore((s) => s.assign);
    // Quản lý xem được cả 4 góc nhìn; nhân viên vào thẳng góc nhìn theo phòng ban
    const role: Role = canManage ? storedView ?? "ops" : me.role ?? "tele";

    const [searchParams, setSearchParams] = useSearchParams();
    const [initialFilters] = useState<CustomerListFilters>(() => parseFilters(searchParams));

    const [customerCareStatusId, setCustomerCareStatusId] = useState<number | null>(initialFilters.tagId);
    const [selectedRowKeys, setSelectedRowKeys] = useState<React.Key[]>([]);
    const [openCreate, setOpenCreate] = useState(false);
    const [deleteCustomer, setDeleteCustomer] = useState<ChannelCustomerItem | null>(null);
    const [selected, setSelected] = useState<{ rec: ChannelCustomerItem; action?: LeadAction; seq: number } | null>(null);
    const [inCall, setInCall] = useState(false);
    // Mặc định chỉ hiện khách tiềm năng (Mới → S6); bật để xem cả khách đã làm dịch vụ
    const [showAll, setShowAll] = useState(false);
    // Giữ thứ tự sắp xếp ở đây: để Table tự giữ thì hàm so sánh cũ không biết khách mới cần ghim
    const [deadlineSort, setDeadlineSort] = useState<"ascend" | "descend" | null>("ascend");
    /** Thẻ hiện ở danh sách theo cấu hình ma trận: undefined = chưa tải, null = dòng cấu hình đang tắt */
    const [listTagIds, setListTagIds] = useState<number[] | null | undefined>();
    const [quick, setQuick] = useState<string | null>(null);
    // Khách vừa tạo ở màn này: luôn nằm đầu danh sách (mới nhất trước) cho tới khi tải lại trang
    const [freshIds, setFreshIds] = useState<number[]>([]);
    const [assignTo, setAssignTo] = useState<number>();
    const [teleStaffs, setTeleStaffs] = useState<{ value: number; label: string }[]>([]);
    const [pageStaffs, setPageStaffs] = useState<{ id: number; name: string }[]>([]);
    useEffect(() => {
        if (!canManage) return;
        mixGetV1("/api/v1/be/staffs", { type_user: 1, limit: 200, type_department_id: 2 })
            .then((res) => {
                const items = Array.isArray(res?.data?.items) ? res.data.items : Array.isArray(res?.data) ? res.data : [];
                setPageStaffs(items.filter((x: any) => x?.id).map((x: any) => ({ id: Number(x.id), name: x.fullname || x.name || `#${x.id}` })));
            })
            .catch(() => undefined);
    }, [canManage]);
    const [tags, setTags] = useState<CustomerTagItem[]>([]);
    const [now, setNow] = useState(Date.now());
    const leads = useLeadStore((s) => s.byId);
    const stickyRef = useRef<HTMLDivElement>(null);
    const [stickyH, setStickyH] = useState(0);
    // Nhớ chế độ mở rộng của ngăn hồ sơ: mở khách khác / gọi vẫn giữ nguyên
    const [drawerWide, setDrawerWideState] = useState(() => {
        try { return localStorage.getItem(DRAWER_WIDE_KEY) === '1'; } catch { return false; }
    });
    const setDrawerWide = (v: boolean) => {
        setDrawerWideState(v);
        try { localStorage.setItem(DRAWER_WIDE_KEY, v ? '1' : '0'); } catch { /* bỏ qua */ }
    };
    const [showFilters, setShowFilters] = useState(() => {
        try { return localStorage.getItem(FILTERS_OPEN_KEY) === '1'; } catch { return false; }
    });
    const toggleFilters = () => setShowFilters((v) => {
        try { localStorage.setItem(FILTERS_OPEN_KEY, v ? '0' : '1'); } catch { /* bỏ qua */ }
        return !v;
    });
    const narrow = useMedia("(max-width: 639px)");
    // Vùng danh sách đủ rộng cho cả hàng bộ lọc thì bung hết; hẹp hơn (màn nhỏ, hoặc đang mở hồ sơ bên phải) thì thu vào nút "Bộ lọc"
    const listRef = useRef<HTMLDivElement>(null);
    const [listW, setListW] = useState(() => window.innerWidth);
    useEffect(() => {
        const el = listRef.current;
        if (!el) return undefined;
        const ro = new ResizeObserver(() => setListW(el.clientWidth));
        ro.observe(el);
        return () => ro.disconnect();
    }, []);
    const wide = listW >= FILTERS_ROW_MIN;
    // Vùng danh sách hẹp (mở hồ sơ, tablet, mobile): bảng gọn 3 cột, nút thao tác chỉ còn biểu tượng
    const compact = listW < COMPACT_TABLE_MAX;
    const iconOnly = narrow || compact;
    useEffect(() => {
        const el = stickyRef.current;
        if (!el) return undefined;
        const ro = new ResizeObserver(() => setStickyH(el.offsetHeight));
        ro.observe(el);
        return () => ro.disconnect();
    }, [role]);
    const demo = useLeadStore((s) => s.demo);
    const seedDemo = useLeadStore((s) => s.seedDemo);
    const clearDemo = useLeadStore((s) => s.clearDemo);
    const [localPage, setLocalPage] = useState(1);
    const [careStatuses, setCareStatuses] = useState<CareStatusItem[]>([]);
    useEffect(() => { fetchCareStatuses().then(setCareStatuses).catch(() => undefined); }, []);
    const [seeding, setSeeding] = useState(false);

    useEffect(() => {
        getCustomerTags({ sort: ["id,asc"] }).then((res) => { if (res.status && res.data) setTags(res.data.items); }).catch(() => undefined);
        const id = setInterval(() => setNow(Date.now()), 30_000);
        return () => clearInterval(id);
    }, []);
    useEffect(() => {
        if (!canManage || teleStaffs.length) return;
        mixGetV1("/api/v1/be/staffs", { type_user: 1, limit: 200, type_department_id: 3 })
            .then((res) => {
                const items = Array.isArray(res?.data?.items) ? res.data.items : Array.isArray(res?.data) ? res.data : [];
                setTeleStaffs(items.filter((s: any) => s?.id).map((s: any) => ({ value: Number(s.id), label: s.fullname || s.name || `#${s.id}` })));
            })
            .catch(() => undefined);
    }, [canManage, teleStaffs.length]);

    const [data, setData] = useState<ChannelCustomerItem[]>([]);
    const [loading, setLoading] = useState(false);
    const [searchText, setSearchText] = useState(initialFilters.search);
    const [page, setPage] = useState(initialFilters.page);
    const [total, setTotal] = useState(0);
    // Telesales / Trực page mặc định chỉ thấy khách của mình
    const [staffId, setStaffId] = useState<number | undefined>(initialFilters.staffId ?? (role === "tele" ? me.staffId ?? undefined : undefined));
    const [pageStaffId, setPageStaffId] = useState<number | undefined>(initialFilters.pageStaffId ?? (role === "page" ? me.staffId ?? undefined : undefined));
    const [startDate, setStartDate] = useState<string | undefined>(initialFilters.startDate);
    const [endDate, setEndDate] = useState<string | undefined>(initialFilters.endDate);
    const [oauthAccountIds, setOauthAccountIds] = useState<number[]>(initialFilters.oauthAccountIds);
    const [channelConnectionIds, setChannelConnectionIds] = useState<number[]>(initialFilters.channelConnectionIds);
    const [careStatusIds, setCareStatusIds] = useState<number[]>(initialFilters.careStatusIds);
    const [period, setPeriod] = useState<string>(initialFilters.period);
    const [refDate, setRefDate] = useState<string | undefined>(initialFilters.refDate);
    const [statusRefreshKey, setStatusRefreshKey] = useState(0);
    const [deleteLoading, setDeleteLoading] = useState(false);
    const debouncedSearch = useDebounce(searchText, 400);

    useEffect(() => {
        const params = buildFilterParams({
            search: debouncedSearch,
            tagId: customerCareStatusId,
            page,
            staffId,
            pageStaffId,
            startDate,
            endDate,
            period,
            refDate,
            oauthAccountIds,
            channelConnectionIds,
            careStatusIds,
        });
        saveCustomerListQuery(params.toString());
        setSearchParams(params, { replace: true });
    }, [setSearchParams, debouncedSearch, customerCareStatusId, page, staffId, pageStaffId, startDate, endDate, period, refDate, oauthAccountIds, channelConnectionIds, careStatusIds]);

    const fetchCustomers = useCallback(async () => {
        setLoading(true);
        try {
            const res = await mixGetV1('/api/v2/telesale/customers', {
                include_all: showAll ? 1 : undefined,
                search: debouncedSearch || undefined,
                customer_tag_id: customerCareStatusId || undefined,
                contact_staff_id: staffId || undefined,
                in_charge_staff_id: pageStaffId || undefined,
                start_date: startDate || undefined,
                end_date: endDate || undefined,
                oauth_account_id: oauthAccountIds.length ? oauthAccountIds : undefined,
                channel_connection_id: channelConnectionIds.length ? channelConnectionIds : undefined,
                customer_care_status_ids: careStatusIds.length ? careStatusIds : undefined,
                page,
            });
            if (res?.status && res.data) {
                setData(res.data.items);
                setTotal(res.data.total || 0);
                setListTagIds(res.data.list_tag_ids);
                return res.data.items as ChannelCustomerItem[];
            }
        } catch {
            // Mất mạng / máy chủ không phản hồi: giữ danh sách đang có, lần làm mới sau tự tải lại
            message.warning({ key: "customers-offline", content: "Không kết nối được máy chủ, đang giữ danh sách cũ." });
        } finally {
            setLoading(false);
        }
    }, [showAll, debouncedSearch, customerCareStatusId, staffId, pageStaffId, startDate, endDate, oauthAccountIds, channelConnectionIds, careStatusIds, page]);

    useEffect(() => { fetchCustomers(); }, [fetchCustomers]);

    // Hồ sơ đổi thẻ, nhu cầu, cọc qua API → cập nhật dòng khách và số trên dãy thẻ
    useEffect(() => onCustomerPatched((id, patch) => {
        setData((d) => d.map((r) => (r.id === id ? { ...r, ...patch } : r)));
        setSelected((s) => (s && s.rec.id === id ? { ...s, rec: { ...s.rec, ...patch } } : s));
        if (patch.customer_tag) setStatusRefreshKey((k) => k + 1);
    }), []);

    // Đổi bộ lọc → về trang 1
    const handleSearch = useCallback((v: string) => { setPage(1); setSearchText(v); }, []);
    const handleStaffChange = useCallback((v?: number) => { setPage(1); setStaffId(v); }, []);
    const handlePageStaffChange = useCallback((v?: number) => { setPage(1); setPageStaffId(v); }, []);
    const handleDateChange = useCallback((start?: string, end?: string, nextPeriod?: string, nextRefDate?: string) => {
        setPage(1);
        setStartDate(start);
        setEndDate(end);
        if (nextPeriod) setPeriod(nextPeriod);
        setRefDate(nextRefDate);
    }, []);
    const handleSourceChange = useCallback((oauthIds: number[], connectionIds: number[]) => {
        setPage(1);
        setOauthAccountIds(oauthIds);
        setChannelConnectionIds(connectionIds);
    }, []);
    const handleCareStatusChange = useCallback((ids: number[]) => { setPage(1); setCareStatusIds(ids); }, []);

    const switchRole = (r: Role) => {
        if (inCall) { message.warning("Đang có cuộc gọi chưa lưu kết quả."); return; }
        setView(r);
        setQuick(null);
        setSelected(null);
        setSelectedRowKeys([]);
        setPage(1);
        setStaffId(r === "tele" ? me.staffId ?? undefined : undefined);
        setPageStaffId(r === "page" ? me.staffId ?? undefined : undefined);
    };

    /* ---------- Khách mẫu (chỉ trên máy này): lọc phía trình duyệt theo cùng bộ lọc với API ---------- */
    const demoBase = useMemo(() => {
        if (!demo) return [] as DemoCustomer[];
        // Lọc theo nguồn kết nối / trạng thái chăm sóc chỉ áp được cho khách thật
        if (oauthAccountIds.length || channelConnectionIds.length) return [];
        const kw = debouncedSearch.trim().toLowerCase();
        return enrichDemo(demo.customers, tags, careStatuses)
            .filter((c) => {
                const staff = staffOf(c, leads[c.id]);
                const day = dayjs(c.created_at).format("YYYY-MM-DD");
                return (!kw || [c.display_name, c.phone, c.customer_code].some((v) => String(v || "").toLowerCase().includes(kw)))
                    && (!staffId || staff?.id === staffId)
                    && (!pageStaffId || c.in_charge_staff?.id === pageStaffId)
                    && (!startDate || day >= startDate)
                    && (!endDate || day <= endDate)
                    && (!careStatusIds.length || chatTagsOf(c, leads[c.id]).some((x) => careStatusIds.includes(x.id)));
            });
    }, [careStatuses, demo, tags, leads, debouncedSearch, staffId, pageStaffId, startDate, endDate, oauthAccountIds, channelConnectionIds, careStatusIds]);
    /** Số khách mẫu theo thẻ hiệu lực — cộng vào chip mã S */
    const demoCounts = useMemo(() => {
        const out: Record<number, number> = {};
        demoBase.forEach((c) => {
            const t = tagOf(c, leads[c.id], tags);
            if (t && t.id > 0) out[t.id] = (out[t.id] ?? 0) + 1;
        });
        return out;
    }, [demoBase, leads, tags]);
    const demoRows = useMemo(
        () => (customerCareStatusId ? demoBase.filter((c) => tagOf(c, leads[c.id], tags)?.id === customerCareStatusId) : demoBase),
        [demoBase, customerCareStatusId, leads, tags],
    );
    const demoMode = demoRows.length > 0;
    // Có khách mẫu thì gộp với trang 1 của API và phân trang ngay trên trình duyệt
    const allData = useMemo(() => (demoRows.length ? [...demoRows, ...data] : data), [demoRows, data]);
    useEffect(() => { setLocalPage(1); }, [demoBase.length, customerCareStatusId, quick, role]);
    useEffect(() => { if (demoMode && page !== 1) setPage(1); }, [demoMode, page]);

    const createDemo = async () => {
        setSeeding(true);
        const load = (dept: number) => mixGetV1("/api/v1/be/staffs", { type_user: 1, limit: 200, type_department_id: dept })
            .then((res) => (Array.isArray(res?.data?.items) ? res.data.items : Array.isArray(res?.data) ? res.data : []) as any[])
            .then((items) => items.filter((x) => x?.id).slice(0, 4).map((x) => ({ id: Number(x.id), name: x.fullname || x.name || `#${x.id}` })) as Staff[])
            .catch(() => [] as Staff[]);
        const [teles, pages] = await Promise.all([load(3), load(2)]);
        const { customers, locals } = buildDemo(
            Date.now(),
            teles.length ? teles : [{ id: 13, name: "tele admin" }],
            pages.length ? pages : [{ id: 12, name: "truc page admin" }],
        );
        seedDemo(customers, locals);
        setSeeding(false);
        setPage(1);
        message.success(`Đã thêm ${customers.length} khách mẫu (chỉ lưu trên máy này)`);
    };

    /* ---------- Dòng hiển thị: thẻ hiệu lực (đã đổi sau cuộc gọi) quyết định có phải khách tiềm năng ---------- */
    const stepOf = useCallback((r: ChannelCustomerItem) => nextStep(r, leads[r.id], now, tagOf(r, leads[r.id], tags)?.name), [leads, now, tags]);
    const isListed = useCallback((r: ChannelCustomerItem) => {
        const tag = tagOf(r, leads[r.id], tags);
        return isDemoId(r.id) ? isLeadTag(tag?.name) : inLeadList(tag, listTagIds);
    }, [leads, tags, listTagIds]);
    const leadRows = useMemo(
        () => (showAll || customerCareStatusId ? allData : allData.filter(isListed)),
        [allData, showAll, customerCareStatusId, isListed],
    );
    // Dãy thẻ S nhận theo tên thẻ: đổi list_tag_ids sang tên
    const tabFilter = useMemo(() => {
        if (showAll || listTagIds === null) return undefined;
        if (listTagIds === undefined || !tags.length) return isLeadTag;
        const names = new Set(tags.filter((t) => listTagIds.includes(t.id)).map((t) => t.name));
        return (name: string) => names.has(name);
    }, [showAll, listTagIds, tags]);
    const quicks = useMemo(() => quickDefs(role, now), [role, now]);
    const counts = useMemo(
        () => Object.fromEntries(quicks.map((q) => [q.key, leadRows.filter((r) => q.test(r, leads[r.id], stepOf(r))).length])),
        [quicks, leadRows, leads, stepOf],
    );
    const activeQuick = quicks.find((q) => q.key === quick);

    /* ---------- Chia lại khách khi Telesales không tương tác ---------- */
    const reassignCfg = useLeadStore((s) => s.reassignCfg);
    const rr = useLeadStore((s) => s.rr);
    const runReassign = useLeadStore((s) => s.runReassign);
    const [openReassign, setOpenReassign] = useState(false);
    const leadAll = useMemo(() => allData.filter(isListed), [allData, isListed]);
    const stale = useMemo(() => findStale(leadAll, leads, tags, reassignCfg, now), [leadAll, leads, tags, reassignCfg, now]);
    const staleIds = useMemo(() => new Set(stale.map((x) => x.r.id)), [stale]);
    const staleBlocked = stale.filter((x) => x.blocked).length;
    const ruleText = `quá ${thresholdText(reassignCfg)} không tương tác`;
    const teleList = useMemo(() => teleStaffs.map((x) => ({ id: x.value, name: x.label })), [teleStaffs]);
    const teleLoads = useMemo(() => {
        const out: Record<number, number> = {};
        leadAll.forEach((r) => { const st = staffOf(r, leads[r.id]); if (st) out[st.id] = (out[st.id] ?? 0) + 1; });
        return out;
    }, [leadAll, leads]);
    const nameOf = (r: ChannelCustomerItem) => r.display_name || r.fullname || `KH-${r.id}`;
    // Chia theo nhóm dịch vụ (Nội khoa / Ngoại khoa / Cấy tóc): chỉ giao cho telesale cùng nhóm với khách
    const groupCfg = useGroupCfg((s) => s.cfg);
    const groupRule = useMemo(() => ({ cfg: groupCfg, groupOf: (r: ChannelCustomerItem) => groupOfLead(r, leads[r.id], groupCfg) }), [groupCfg, leads]);
    const doReassign = useCallback((mode: "auto" | "manual") => {
        const { plan, rr: next } = planReassign(stale, teleList, reassignCfg, teleLoads, rr, groupRule);
        if (!plan.length) return;
        runReassign(plan.map((p) => ({ id: p.r.id, customer: nameOf(p.r), from: p.from, to: p.to, idle: p.idle })), mode, me.name, ruleText, next);
        message[mode === "auto" ? "info" : "success"](`${mode === "auto" ? "Tự động chia lại" : "Đã chia tự động"} ${plan.length} khách ${ruleText}`);
    }, [stale, teleList, reassignCfg, teleLoads, rr, runReassign, me.name, ruleText, groupRule]);
    // Tự động: chạy trên máy Người vận hành mỗi lần làm mới (30 giây)
    const autoReady = canManage && reassignCfg.enabled && reassignCfg.mode === "auto" && teleStaffs.length > 0 && stale.some((x) => !x.blocked);
    useEffect(() => { if (autoReady) doReassign("auto"); }, [autoReady, now]); // eslint-disable-line react-hooks/exhaustive-deps

    /* Chat: khách nhắn mà Trực page chưa trả lời */
    const chatCfg = useLeadStore((s) => s.chatCfg);
    const rrChat = useLeadStore((s) => s.rrChat);
    const runChatReassign = useLeadStore((s) => s.runChatReassign);
    const chatStale = useMemo(() => findChatStale(allData, leads, chatCfg, now), [allData, leads, chatCfg, now]);
    const chatStaleIds = useMemo(() => new Set(chatStale.map((x) => x.r.id)), [chatStale]);
    const chatBlocked = chatStale.filter((x) => x.blocked).length;
    const chatRule = `chờ trả lời chat quá ${thresholdText(chatCfg)}`;
    const pageLoads = useMemo(() => {
        const out: Record<number, number> = {};
        allData.forEach((r) => { const st = chatStaffOf(r, leads[r.id]); if (st) out[st.id] = (out[st.id] ?? 0) + 1; });
        return out;
    }, [allData, leads]);
    const doChatReassign = (mode: "auto" | "manual") => {
        const { plan, rr: next } = planReassign(chatStale, pageStaffs, chatCfg, pageLoads, rrChat);
        if (!plan.length) return;
        runChatReassign(plan.map((p) => ({ id: p.r.id, customer: nameOf(p.r), from: p.from, to: p.to, idle: p.idle })), mode, me.name, chatRule, next);
        message[mode === "auto" ? "info" : "success"](`${mode === "auto" ? "Tự động chia lại" : "Đã chia tự động"} ${plan.length} chat ${chatRule}`);
    };
    const autoChat = canManage && chatCfg.enabled && chatCfg.mode === "auto" && pageStaffs.length > 0 && chatStale.some((x) => !x.blocked);
    useEffect(() => { if (autoChat) doChatReassign("auto"); }, [autoChat, now]); // eslint-disable-line react-hooks/exhaustive-deps

    /* Hộp chọn người nhận cho danh sách khách quá thời gian */
    const [pick, setPick] = useState<"call" | "chat" | null>(null);
    const pickItems: StaleLead[] = pick === "chat" ? chatStale : pick === "call" ? stale : [];
    const pickStaffs = pick === "chat" ? pageStaffs : teleList;
    const pickLoads = pick === "chat" ? pageLoads : teleLoads;
    const pickSuggest = useMemo(() => {
        if (!pick) return {};
        const { plan } = pick === "chat"
            ? planReassign(chatStale.map((x) => ({ ...x, blocked: false })), pageStaffs, chatCfg, pageLoads, rrChat)
            : planReassign(stale.map((x) => ({ ...x, blocked: false })), teleList, reassignCfg, teleLoads, rr, groupRule);
        return Object.fromEntries(plan.map((p) => [p.r.id, p.to]));
    }, [pick, chatStale, stale, pageStaffs, teleList, chatCfg, reassignCfg, pageLoads, teleLoads, rrChat, rr, groupRule]);
    const confirmPick = (picks: ReassignPick[]) => {
        const items = picks.map(({ lead, to }) => ({ id: lead.r.id, customer: nameOf(lead.r), from: lead.info.staff, to, idle: lead.info.idle }));
        if (pick === "chat") runChatReassign(items, "manual", me.name, chatRule, rrChat);
        else runReassign(items, "manual", me.name, ruleText, rr);
        message.success(`Đã chia lại ${items.length} khách`);
        setPick(null);
    };

    const filtered = quick === "stale"
        ? leadRows.filter((r) => staleIds.has(r.id))
        : quick === "chatStale" ? leadRows.filter((r) => chatStaleIds.has(r.id))
        : activeQuick ? leadRows.filter((r) => activeQuick.test(r, leads[r.id], stepOf(r))) : leadRows;
    /** Thứ tự ghim của khách vừa tạo (-1 = không ghim) */
    const freshRank = (r: ChannelCustomerItem) => freshIds.indexOf(r.id);
    const rows = freshIds.length
        ? [...filtered.filter((r) => freshRank(r) >= 0).sort((a, b) => freshRank(a) - freshRank(b)), ...filtered.filter((r) => freshRank(r) < 0)]
        : filtered;
    /** So sánh ghim khách mới cho cột có sắp xếp: khách mới luôn ở đầu dù sắp xếp tăng hay giảm */
    const freshCmp = (a: ChannelCustomerItem, b: ChannelCustomerItem, order?: string | null) => {
        const ia = freshRank(a), ib = freshRank(b);
        if (ia < 0 && ib < 0) return 0;
        const d = ia < 0 ? 1 : ib < 0 ? -1 : ia - ib;
        return order === "descend" ? -d : d;
    };
    const hiddenCount = allData.length - leadRows.length;
    const selectedRecord = selected ? allData.find((r) => r.id === selected.rec.id) ?? selected.rec : undefined;
    const readOnly = role === "page" || role === "branch";
    const filterCount = [staffId, pageStaffId, startDate || endDate, oauthAccountIds.length || channelConnectionIds.length, careStatusIds.length].filter(Boolean).length;

    const open = (r: ChannelCustomerItem, action?: LeadAction) => {
        if (inCall && selected?.rec.id !== r.id) {
            message.warning("Đang có cuộc gọi chưa lưu kết quả — lưu kết quả trước khi mở khách khác.");
            return;
        }
        setSelected((s) => ({ rec: r, action, seq: (s?.seq ?? 0) + 1 }));
    };
    const openNext = () => {
        const idx = rows.findIndex((r) => r.id === selected?.rec.id);
        const next = rows.slice(idx + 1).find((r) => r.phone && leads[r.id]?.kenh !== "Chỉ nhắn tin");
        if (!next) { message.info("Đã hết khách cần gọi trong danh sách này"); return; }
        setSelected((s) => ({ rec: next, action: "call", seq: (s?.seq ?? 0) + 1 }));
    };
    const doAssign = () => {
        const staff = teleStaffs.find((s) => s.value === assignTo);
        if (!staff || !selectedRowKeys.length) return;
        assign(selectedRowKeys.map(Number), { id: staff.value, name: staff.label }, me.name);
        message.success(`Đã phân ${selectedRowKeys.length} khách cho ${staff.label}`);
        setSelectedRowKeys([]);
        setAssignTo(undefined);
    };

    const allColumns: SettingColumn<ChannelCustomerItem>[] = [
        {
            // Chế độ gọn: gộp tên, mã S, mã khách, SĐT, Telesales vào một cột để khỏi cuộn ngang
            key: "compact",
            title: "KHÁCH HÀNG",
            width: 300,
            render: (_, r) => {
                const tag = tagOf(r, leads[r.id], tags);
                const s = staffOf(r, leads[r.id]);
                return (
                    <button type="button" onClick={() => open(r, role === "page" ? "needs" : undefined)} className="block w-full min-w-0 text-left">
                        <div className="flex min-w-0 items-center gap-1.5">
                            <span className="truncate font-semibold text-[#3e91ab] hover:underline">{r.display_name || r.fullname || "Khách"}</span>
                            {isDemoId(r.id) && <span className="shrink-0 rounded bg-amber-100 px-1 text-[10px] font-semibold text-amber-700 dark:bg-amber-500/20 dark:text-amber-300">Mẫu</span>}
                            {r.has_received_deposit && <DepositMark />}
                            {tag && (
                                <Tooltip title={tag.name}>
                                    <span className="shrink-0 rounded-full px-2 py-px text-[11px] font-bold" style={{ background: tag.color || "#64748b", color: textOn(tag.color) }}>{shortTag(tag.name)}</span>
                                </Tooltip>
                            )}
                        </div>
                        <div className="truncate text-[12px] text-gray-400">
                            {r.customer_code || `KH-${r.id}`}{r.phone ? ` · ${maskPhone(r.phone)}` : ""} · {s ? s.name : <span className="text-orange-500">Chưa phân</span>}
                        </div>
                    </button>
                );
            },
        },
        {
            key: "customerName",
            title: "KHÁCH HÀNG",
            width: 220,
            render: (_, r) => (
                <button type="button" onClick={() => open(r, role === "page" ? "needs" : undefined)} className="min-w-0 text-left">
                    <div className="flex min-w-0 items-center gap-1.5">
                        <span className="truncate font-semibold text-[#3e91ab] hover:underline">{r.display_name || r.fullname || "Khách"}</span>
                        {r.has_received_deposit && <DepositMark />}
                        {isDemoId(r.id) && <Tooltip title="Khách mẫu — chỉ lưu trên máy này"><span className="shrink-0 rounded bg-amber-100 px-1 text-[10px] font-semibold text-amber-700 dark:bg-amber-500/20 dark:text-amber-300">Mẫu</span></Tooltip>}
                    </div>
                    <div className="text-[12px] text-gray-400">{r.customer_code || `KH-${r.id}`}{r.province?.name ? ` · ${r.province.name}` : ""}</div>
                </button>
            ),
        },
        {
            key: "phone",
            title: "SĐT",
            width: 120,
            render: (_, r) => (r.phone ? <span className="tabular-nums text-gray-700 dark:text-gray-300">{maskPhone(r.phone)}</span> : <Muted>Chưa có</Muted>),
        },
        {
            key: "csCode",
            title: "MÃ S",
            width: 90,
            render: (_, r) => {
                const tag = tagOf(r, leads[r.id], tags);
                if (!tag) return <Muted>—</Muted>;
                return (
                    <Tooltip title={tag.local ? `${tag.name} · đổi trên máy này, chờ đồng bộ` : tag.name}>
                        <span className="inline-flex items-center gap-1 rounded-full px-2.5 py-0.5 text-[12px] font-bold" style={{ background: tag.color || "#64748b", color: textOn(tag.color) }}>
                            {shortTag(tag.name)}{tag.local && <span className="h-1.5 w-1.5 rounded-full bg-current opacity-70" />}
                        </span>
                    </Tooltip>
                );
            },
        },
        {
            key: "csStatus",
            title: "TRẠNG THÁI CS",
            width: 190,
            render: (_, r) => <CsStatusPicker r={r} tags={tags} />,
        },
        {
            key: "chatTags",
            title: "THẺ CHAT",
            width: 200,
            defaultHidden: role !== "page",
            render: (_, r) => <ChatTagPicker r={r} all={careStatuses} />,
        },
        {
            key: "deadline",
            title: "VIỆC TIẾP / HẠN",
            width: 200,
            sortOrder: deadlineSort,
            sorter: (a, b, order) => freshCmp(a, b, order) || (stepOf(a).due ?? Infinity) - (stepOf(b).due ?? Infinity),
            render: (_, r) => {
                const step = stepOf(r);
                const info = idleInfo(r, leads[r.id], tags, reassignCfg, now);
                // Báo Telesales trước khi khách bị chia cho người khác
                const warn = role === "tele" && info.eligible && info.staff && info.idle >= info.threshold * 0.6
                    ? (info.idle >= info.threshold
                        ? <div className="mt-0.5 text-[11.5px] font-semibold text-red-600">Quá {thresholdText(reassignCfg)} chưa tương tác · chờ chia lại</div>
                        : <div className="mt-0.5 text-[11.5px] font-medium text-amber-600">Sắp chia lại · còn {idleText(info.threshold - info.idle)}</div>)
                    : null;
                if (step.kind === "none" || !step.due) return warn ?? <Muted>—</Muted>;
                const due = dueText(step.due, now);
                return (
                    <div className="leading-tight">
                        <div className="text-[13px] text-gray-700 dark:text-gray-300">{step.label}</div>
                        <div className={`text-[12px] font-semibold ${due.late ? "text-red-600" : "text-[#2c6b7f] dark:text-[#8cc8da]"}`}>{due.text}</div>
                        {warn}
                    </div>
                );
            },
        },
        {
            key: "progress",
            title: "TÌNH TRẠNG XỬ LÝ",
            width: 230,
            render: (_, r) => {
                const p = progressOf(r, leads[r.id], now);
                const cls = p.tone === "late" ? "text-red-600 font-semibold" : p.tone === "warn" ? "text-amber-600" : "text-emerald-600";
                const last = lastTouchOf(r, leads[r.id]);
                return (
                    <div className="leading-tight">
                        <div className={`text-[13px] ${cls}`}>{p.text}</div>
                        {last && <div className="text-[12px] text-gray-400">Lần chạm cuối {when(last.at, now)}</div>}
                    </div>
                );
            },
        },
        {
            key: "serviceCategory",
            title: "MẢNG",
            width: 100,
            render: (_, r) => mangOf(r, leads[r.id]) || <Muted>—</Muted>,
        },
        {
            key: "source",
            title: "NGUỒN",
            width: 160,
            render: (_, r) => sourceOf(r) || <Muted>—</Muted>,
        },
        {
            key: "pageStaff",
            title: "TRỰC PAGE TẠO",
            width: 160,
            render: (_, r) => {
                const l = leads[r.id];
                const chat = chatStaffOf(r, l);
                const info = chatIdleInfo(r, l, chatCfg, now);
                return (
                    <div className="leading-tight">
                        {r.in_charge_staff?.name ? <div className="text-gray-700 dark:text-gray-300">{r.in_charge_staff.name}</div> : <Muted>—</Muted>}
                        {l?.chatAssign && chat && <div className="text-[12px] text-[#2c6b7f] dark:text-[#8cc8da]">Đang chat: {chat.name} <span className="inline-block h-1.5 w-1.5 rounded-full bg-[#3e91ab] align-middle" /></div>}
                        {info.eligible && (
                            <div className={`text-[11.5px] font-semibold ${chatStaleIds.has(r.id) ? "text-red-600" : "text-orange-500"}`}>Khách chờ trả lời {idleText(info.idle)}</div>
                        )}
                    </div>
                );
            },
        },
        {
            key: "staff",
            title: "TELESALES",
            width: 180,
            render: (_, r) => {
                const s = staffOf(r, leads[r.id]);
                return (
                    <div className="leading-tight">
                        <div className="text-gray-700 dark:text-gray-300">
                            {s ? <Tooltip title={s.local ? "Phân công trên máy này, chờ đồng bộ" : ""}><span>{s.name}{s.local && <span className="ml-1 inline-block h-1.5 w-1.5 rounded-full bg-[#3e91ab] align-middle" />}</span></Tooltip> : <span className="text-orange-500">Chưa phân</span>}
                        </div>
                        {staleIds.has(r.id) && (
                            <div className="mt-0.5 text-[11.5px] font-semibold text-red-600">
                                {(leads[r.id]?.reassignCount ?? 0) >= reassignCfg.maxTimes
                                    ? `Đã chia lại ${leads[r.id]?.reassignCount} lần · cần xử lý tay`
                                    : `${idleText(idleInfo(r, leads[r.id], tags, reassignCfg, now).idle)} chưa tương tác`}
                            </div>
                        )}
                        {!staleIds.has(r.id) && (leads[r.id]?.reassignCount ?? 0) > 0 && (
                            <div className="mt-0.5 text-[11.5px] text-gray-400">Đã chia lại {leads[r.id]?.reassignCount} lần</div>
                        )}
                    </div>
                );
            },
        },
        {
            key: "createdAt",
            title: "NGÀY TẠO",
            width: 110,
            render: (_, r) => (r.created_at ? <span className="text-gray-700 dark:text-gray-300">{dayjs(r.created_at).format("DD/MM HH:mm")}</span> : <Muted>—</Muted>),
        },
        {
            key: "lastTouch",
            title: "LẦN CHẠM CUỐI",
            width: 150,
            render: (_, r) => {
                const tch = lastTouchOf(r, leads[r.id]);
                if (!tch) return <Muted>Chưa chạm</Muted>;
                return <span className="text-gray-700 dark:text-gray-300">{when(tch.at, now)}{tch.code ? ` · ${tch.code}` : ""}</span>;
            },
        },
        {
            key: "branch",
            title: "LỊCH HẸN",
            width: 170,
            render: (_, r) => {
                const a = activeAppt(leads[r.id], now);
                if (!a) return <Muted>—</Muted>;
                return (
                    <div className="leading-tight">
                        <div className="text-gray-700 dark:text-gray-300">{dayjs(a.at).format("HH:mm DD/MM")}</div>
                        <div className="text-[12px] text-gray-400">{a.branch}</div>
                    </div>
                );
            },
        },
        {
            key: "actions",
            title: "",
            align: "center",
            width: iconOnly ? (role === "page" ? 96 : 112) : role === "page" ? 190 : 150,
            fixed: "right",
            lockVisible: true,
            render: (_, r) => {
                if (role === "page") {
                    return (
                        <div className="flex items-center justify-center gap-1.5">
                            <Button size="small" icon={<Eye size={13} />} onClick={() => open(r)} aria-label="Xem">{!iconOnly && "Xem"}</Button>
                            <Button size="small" type="primary" icon={<ClipboardEdit size={13} />} onClick={() => open(r, "needs")} className="!bg-[#3e91ab]" aria-label="Nhu cầu">{!iconOnly && "Nhu cầu"}</Button>
                        </div>
                    );
                }
                const onlyMsg = leads[r.id]?.kenh === "Chỉ nhắn tin";
                return (
                    <div className="flex items-center justify-center gap-1.5">
                        <Tooltip title={!r.phone ? "Chưa có số điện thoại" : onlyMsg ? "Khách chỉ muốn nhắn tin" : ""}>
                            <Button size="small" type="primary" disabled={!r.phone || onlyMsg} onClick={() => open(r, "call")} icon={<Phone size={13} />} className="!bg-[#3e91ab] disabled:!bg-gray-200" aria-label="Gọi">{!iconOnly && "Gọi"}</Button>
                        </Tooltip>
                        <Button size="small" onClick={() => open(r, "msg")} icon={<MessageCircle size={13} />} aria-label="Zalo">{!iconOnly && "Zalo"}</Button>
                        <Dropdown
                            trigger={["click"]}
                            menu={{
                                items: [
                                    { key: "appt", label: "Đặt lịch hẹn", icon: <CalendarPlus size={14} />, onClick: () => open(r, "appt") },
                                    ...(role === "ops" ? [{ key: "assign", label: "Phân công lại", icon: <Users size={14} />, onClick: () => setSelectedRowKeys([r.id]) }] : []),
                                    ...(canDelete && !isDemoId(r.id) ? [{ type: "divider" as const }, { key: "del", danger: true, label: "Xoá khách", icon: <Trash2 size={14} />, onClick: () => setDeleteCustomer(r) }] : []),
                                ],
                            }}
                        >
                            <Button size="small" type="text" icon={<MoreHorizontal size={16} />} aria-label="Thêm thao tác" />
                        </Dropdown>
                    </div>
                );
            },
        },
    ];

    const roleCols = role === "branch" ? [] : compact ? ["compact", role === "page" ? "progress" : "deadline", "actions"] : ROLE_COLUMNS[role];
    const columns = allColumns.filter((c) => roleCols.includes(c.key as string));

    const {
        columns: tableColumns,
        components: tableComponents,
        tableLayout,
        scrollX,
        settingProps: columnSettingProps,
    } = useTableColumnSettings(`tele_lead_list_${role}${compact ? "_compact" : ""}`, columns, { extraWidth: role === "ops" ? SELECTION_COL_WIDTH : 0 });

    const handleDeleteCustomer = async () => {
        const customerId = deleteCustomer?.customer?.id ?? deleteCustomer?.id;
        if (!customerId) return;
        setDeleteLoading(true);
        const res = await deleteCustomerApi(customerId);
        setDeleteLoading(false);
        if (res.status) {
            message.success(t('customer.delete_success'));
            if (selected?.rec.id === deleteCustomer?.id) setSelected(null);
            setDeleteCustomer(null);
            fetchCustomers();
            setStatusRefreshKey((k) => k + 1);
        } else {
            message.error(extractApiError(res, i18n.language) || t('customer.delete_failed'));
        }
    };

    const RoleIcon = ROLE_META[role].icon;
    const scopeNote = (role === "tele" || role === "page") && !me.staffId
        ? `Tài khoản này không gắn nhân viên nên đang thấy khách của mọi người — chọn ${role === "tele" ? "Telesales" : "Trực page"} ở bộ lọc để xem như một nhân viên.`
        : null;

    return (
        <Layout>
            <Helmet>
                <title>Khách tiềm năng | Khothi</title>
            </Helmet>

            <div className="flex w-full items-start gap-4 text-sm">
                <div ref={listRef} className="min-w-0 flex-1 space-y-3 pb-16">
                    {/* Tiêu đề + góc nhìn + thao tác chính — gom một hàng cho gọn */}
                    <div className="flex flex-wrap items-center gap-x-3 gap-y-2">
                        {/* Giữ tối thiểu 240px cho tiêu đề; thiếu chỗ thì nhóm nút xuống hàng dưới */}
                        <div className="flex min-w-[240px] flex-1 items-center gap-2.5">
                            <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-xl bg-[#e8f4f7] text-[#3e91ab] dark:bg-[#3e91ab]/20"><RoleIcon size={18} /></span>
                            <div className="min-w-0">
                                <h1 className="m-0 truncate text-[17px] font-bold leading-tight text-gray-900 dark:text-gray-50">
                                    Khách tiềm năng{!canManage && <span className="ml-2 text-[13px] font-medium text-[#3e91ab]">· {ROLE_META[role].label}</span>}
                                </h1>
                                <div className="truncate text-[12px] text-gray-500 dark:text-gray-400">{ROLE_META[role].hint}</div>
                            </div>
                        </div>
                        <div className="flex max-w-full items-center gap-2">
                            {canManage && (
                                // Màn hẹp: thanh góc nhìn cuộn ngang thay vì tràn
                                <div className="min-w-0 overflow-x-auto [scrollbar-width:none] [&::-webkit-scrollbar]:hidden">
                                    <Segmented
                                        value={role}
                                        onChange={(v) => switchRole(v as Role)}
                                        options={ROLES.map((r) => {
                                            const Icon = ROLE_META[r].icon;
                                            return { value: r, label: <span className="flex items-center gap-1.5 whitespace-nowrap px-0.5"><Icon size={14} />{ROLE_META[r].label}</span> };
                                        })}
                                    />
                                </div>
                            )}
                            {canManage && (
                                <Tooltip title="Cài đặt chia lại khách">
                                    <Button icon={<Shuffle size={15} />} onClick={() => setOpenReassign(true)} aria-label="Cài đặt chia lại" className="shrink-0" />
                                </Tooltip>
                            )}
                            {(role === "tele" || role === "ops") && (
                                // Gọi theo hàng đợi làm ở Tổng đài; màn này để tra cứu, chia khách
                                <Tooltip title="Gọi lần lượt theo hàng đợi (cuộc nhỡ, hẹn gọi lại, khách mới…)">
                                    <Link to="/telesales/call-center"><Button icon={<Headset size={15} />} className="shrink-0"><span className="hidden sm:inline">Tổng đài</span></Button></Link>
                                </Tooltip>
                            )}
                            {role !== "branch" && (
                                <Button type="primary" icon={<Plus size={15} />} onClick={() => setOpenCreate(true)} className="shrink-0 !bg-[#3e91ab] font-semibold">
                                    <span className="hidden sm:inline">Thêm khách</span>
                                </Button>
                            )}
                        </div>
                    </div>

                    {role === "branch" ? (
                        <BranchBoard data={allData} tags={tags} me={me.name} now={now} onOpen={(r) => open(r, "appt")} />
                    ) : (
                        <>
                            <Toolbar
                                key={`toolbar-${role}`}
                                showCreate={false}
                                expanded={wide || showFilters}
                                extra={!wide && (
                                    <Tooltip title={showFilters ? 'Thu gọn bộ lọc' : 'Lọc theo Telesales, Trực page, ngày tạo, nguồn, thẻ chat'}>
                                        <Button
                                            icon={<SlidersHorizontal size={14} />}
                                            onClick={toggleFilters}
                                            className={`h-[36px] shrink-0 ${filterCount ? "!border-[#3e91ab] !text-[#3e91ab]" : ""}`}
                                        >
                                            Bộ lọc{filterCount ? ` (${filterCount})` : ""}
                                            <ChevronDown size={14} className={`transition ${showFilters ? "rotate-180" : ""}`} />

                                        </Button>
                                    </Tooltip>
                                )}
                                onCreate={() => setOpenCreate(true)}
                                onSearch={handleSearch}
                                onStaffChange={handleStaffChange}
                                onPageStaffChange={handlePageStaffChange}
                                onDateChange={handleDateChange}
                                onSourceChange={handleSourceChange}
                                onCareStatusChange={handleCareStatusChange}
                                initialFilters={{ search: searchText, staffId, pageStaffId, period, refDate, startDate, endDate, oauthAccountIds, channelConnectionIds, careStatusIds }}
                            />

                            {/* Ô số liệu gọn một hàng = lọc nhanh (mobile: vuốt ngang) */}
                            <div className="flex flex-col gap-2 md:flex-row md:flex-wrap md:items-center">
                                <div className="-mx-1 flex gap-2 overflow-x-auto px-1 [scrollbar-width:none] md:mx-0 md:flex-wrap md:overflow-visible md:px-0 [&::-webkit-scrollbar]:hidden">
                                {quicks.map((q) => {
                                    const n = counts[q.key] ?? 0;
                                    const on = quick === q.key;
                                    return (
                                        <Tooltip key={q.key} title={q.hint}>
                                            <button
                                                type="button"
                                                onClick={() => setQuick(on ? null : q.key)}
                                                className={`inline-flex shrink-0 items-center gap-2 rounded-lg border bg-white py-1.5 pl-1.5 pr-3 text-left transition hover:border-[#3e91ab] dark:bg-gray-800 ${on ? "border-[#3e91ab] ring-1 ring-[#3e91ab]" : q.key === "late" && n ? "border-red-200 dark:border-red-500/40" : "border-gray-200 dark:border-gray-700"}`}
                                            >
                                                <span className={`flex h-6 w-6 shrink-0 items-center justify-center rounded-md ${PILL_TONE[q.tone]}`}>{q.icon}</span>
                                                <span className="whitespace-nowrap text-[12.5px] text-gray-600 dark:text-gray-300">{q.label}</span>
                                                <span className={`text-[15px] font-bold tabular-nums ${q.key === "late" && n ? "text-red-500" : "text-gray-900 dark:text-white"}`}>{n}</span>
                                            </button>
                                        </Tooltip>
                                    );
                                })}
                                </div>
                                <div className="flex flex-wrap items-center gap-x-2 gap-y-1 text-[12px] text-gray-400 md:ml-auto">
                                    <span>Tính trên {leadRows.length} khách đang tải</span>
                                    {(activeQuick || quick === "stale" || quick === "chatStale") && (
                                        <span>· lọc “{activeQuick?.label ?? (quick === "chatStale" ? "Chat chờ trả lời" : "Quá hạn tương tác")}” <button type="button" className="text-[#3e91ab] hover:underline" onClick={() => setQuick(null)}>Bỏ lọc</button></span>
                                    )}
                                    {demo ? (
                                        <span className="inline-flex items-center gap-1.5 rounded-md bg-amber-50 px-2 py-0.5 text-amber-700 dark:bg-amber-500/10 dark:text-amber-300">
                                            <Tooltip title={`Tạo lúc ${dayjs(demo.at).format("HH:mm DD/MM")} — chỉ lưu trên máy này, không có trên server`}>
                                                <span>{demo.customers.length} khách mẫu</span>
                                            </Tooltip>
                                            ·<button type="button" className="hover:underline" disabled={seeding} onClick={createDemo}>Tạo lại</button>
                                            ·<button type="button" className="text-red-500 hover:underline" onClick={() => { clearDemo(); setSelected(null); message.success("Đã xoá khách mẫu"); }}>Xoá</button>
                                        </span>
                                    ) : process.env.NODE_ENV !== "production" && (
                                        <button type="button" disabled={seeding} onClick={createDemo} className="rounded-md border border-dashed border-gray-300 px-2 py-0.5 hover:border-[#3e91ab] hover:text-[#3e91ab] dark:border-gray-600">+ 30 khách mẫu</button>
                                    )}
                                </div>
                            </div>
                            {scopeNote && <div className="-mt-1 text-[12px] text-amber-600">{scopeNote}</div>}

                            {role === "ops" && reassignCfg.enabled && stale.length > 0 && (
                                <div className="flex flex-wrap items-center gap-x-3 gap-y-1.5 rounded-lg border border-red-200 bg-red-50 px-3 py-1.5 dark:border-red-500/30 dark:bg-red-500/10">
                                    <TriangleAlert size={16} className="shrink-0 text-red-500" />
                                    <div className="min-w-[220px] flex-1 text-[12.5px] text-red-700 dark:text-red-300">
                                        <b>{stale.length} khách</b> được giao nhưng {ruleText} (gọi tổng đài / chat).
                                        {staleBlocked > 0 && <> {staleBlocked} khách đã chia lại đủ {reassignCfg.maxTimes} lần — cần xử lý tay.</>}
                                        {reassignCfg.mode === "auto" && <span className="text-red-500/80"> Tự động chia lại đang bật.</span>}
                                    </div>
                                    <div className="ml-auto flex flex-wrap justify-end gap-1.5">
                                        <Button size="small" type="text" onClick={() => setQuick(quick === "stale" ? null : "stale")}>{quick === "stale" ? "Bỏ lọc" : "Xem"}</Button>
                                        {stale.length > staleBlocked && (
                                            <Tooltip title="Chia ngay theo Cài đặt chia lại (ít khách nhất / xoay vòng), không cần chọn từng người">
                                                <Button size="small" danger icon={<Bot size={13} />} onClick={() => doReassign("manual")}>Chia tự động ({stale.length - staleBlocked})</Button>
                                            </Tooltip>
                                        )}
                                        <Button size="small" type="primary" danger icon={<Shuffle size={13} />} onClick={() => setPick("call")}>
                                            Chọn cách chia ({stale.length})
                                        </Button>
                                    </div>
                                </div>
                            )}

                            {(role === "ops" || role === "page") && chatCfg.enabled && chatStale.length > 0 && (
                                <div className="flex flex-wrap items-center gap-x-3 gap-y-1.5 rounded-lg border border-orange-200 bg-orange-50 px-3 py-1.5 dark:border-orange-500/30 dark:bg-orange-500/10">
                                    <MessageCircle size={16} className="shrink-0 text-orange-500" />
                                    <div className="min-w-[220px] flex-1 text-[12.5px] text-orange-800 dark:text-orange-200">
                                        <b>{chatStale.length} khách</b> nhắn tin mà Trực page {chatRule.replace("chờ trả lời chat ", "chưa trả lời ")}.
                                        {chatBlocked > 0 && <> {chatBlocked} khách đã chia lại đủ {chatCfg.maxTimes} lần.</>}
                                        {chatCfg.mode === "auto" && <span className="opacity-80"> Tự động chia lại đang bật.</span>}
                                    </div>
                                    <div className="ml-auto flex flex-wrap justify-end gap-1.5">
                                        <Button size="small" type="text" onClick={() => setQuick(quick === "chatStale" ? null : "chatStale")}>{quick === "chatStale" ? "Bỏ lọc" : "Xem"}</Button>
                                        {canManage && chatStale.length > chatBlocked && (
                                            <Tooltip title="Chia ngay theo Cài đặt chia lại (ít khách nhất / xoay vòng), không cần chọn từng người">
                                                <Button size="small" icon={<Bot size={13} />} onClick={() => doChatReassign("manual")} className="!border-orange-400 !text-orange-600">Chia tự động ({chatStale.length - chatBlocked})</Button>
                                            </Tooltip>
                                        )}
                                        {canManage && (
                                            <Button size="small" type="primary" icon={<Shuffle size={13} />} onClick={() => setPick("chat")} className="!bg-orange-500">
                                                Chọn cách chia ({chatStale.length})
                                            </Button>
                                        )}
                                    </div>
                                </div>
                            )}

                            {/* Ghim khi cuộn: dãy mã S + thanh phân công; tiêu đề bảng dính ngay bên dưới */}
                            <div ref={stickyRef} className="sticky top-[64px] z-20 -mx-2 space-y-2 bg-[#f5f5f5] px-2 py-1.5 dark:bg-[#121212]">
                                <div className="flex items-center gap-3">
                                    <div className="min-w-0 flex-1">
                                        <StatusTabs
                                            activeTagId={customerCareStatusId}
                                            onFilterChange={(statusId) => { setPage(1); setCustomerCareStatusId(statusId); }}
                                            refreshKey={statusRefreshKey}
                                            contactStaffId={staffId}
                                            inChargeStaffId={pageStaffId}
                                            search={debouncedSearch}
                                            startDate={startDate}
                                            endDate={endDate}
                                            oauthAccountIds={oauthAccountIds}
                                            channelConnectionIds={channelConnectionIds}
                                            careStatusIds={careStatusIds}
                                            tagFilter={tabFilter}
                                            extraCounts={demoCounts}
                                        />
                                    </div>
                                    <Tooltip title="Hiện cả khách đã làm dịch vụ (S7, S8) và khách đã chuyển CSKH (S4.1)">
                                        <label className="flex shrink-0 cursor-pointer items-center gap-2 text-[12.5px] text-gray-600 dark:text-gray-300">
                                            <Switch size="small" checked={showAll} onChange={(v) => { setPage(1); setShowAll(v); if (!v) setCustomerCareStatusId(null); }} />
                                            <span className="hidden sm:inline">Gồm khách đã làm DV</span>
                                        </label>
                                    </Tooltip>
                                    <ColumnSettingButton {...columnSettingProps} />
                                </div>

                                {/* Phân công hàng loạt (Người vận hành) */}
                                {role === "ops" && selectedRowKeys.length > 0 && (
                                    <div className="flex flex-wrap items-center gap-2 rounded-lg border border-[#3e91ab]/40 bg-[#e8f4f7] px-3 py-1.5 dark:bg-[#1f3a42]">
                                        <span className="font-semibold text-[#2c6b7f] dark:text-[#8cc8da]">Đã chọn {selectedRowKeys.length} khách</span>
                                        <span className="text-gray-500">· Phân công cho</span>
                                        <Select
                                            size="small"
                                            showSearch
                                            optionFilterProp="label"
                                            placeholder="Chọn Telesales"
                                            value={assignTo}
                                            onChange={setAssignTo}
                                            options={teleStaffs}
                                            className="w-[200px]"
                                        />
                                        <Button size="small" type="primary" disabled={!assignTo} onClick={doAssign} className="!bg-[#3e91ab]">Phân công</Button>
                                        <Button size="small" type="text" onClick={() => setSelectedRowKeys([])}>Bỏ chọn</Button>
                                    </div>
                                )}
                            </div>

                            <Table
                                key={`table-${role}`}
                                rowKey="id"
                                loading={loading}
                                columns={tableColumns}
                                onChange={(_p, _f, s) => { if (!Array.isArray(s) && s.columnKey === "deadline") setDeadlineSort(s.order ?? null); }}
                                components={tableComponents}
                                tableLayout={tableLayout}
                                dataSource={rows}
                                rowSelection={role === "ops" ? { columnWidth: SELECTION_COL_WIDTH, selectedRowKeys, onChange: setSelectedRowKeys } : undefined}
                                rowClassName={(r) => (r.id === selected?.rec.id
                                    ? "!bg-[#e8f4f7] dark:!bg-[#3e91ab]/15 [&>td]:!bg-transparent"
                                    : freshRank(r) >= 0 ? "!bg-emerald-50/70 dark:!bg-emerald-500/10 [&>td]:!bg-transparent" : "")}
                                locale={{
                                    emptyText: (
                                        <Empty
                                            image={Empty.PRESENTED_IMAGE_SIMPLE}
                                            description={activeQuick || quick === "stale"
                                                ? `Không có khách “${activeQuick?.label ?? "Quá hạn tương tác"}” trong trang này`
                                                : hiddenCount
                                                    ? <span>Trang này không có khách tiềm năng · {hiddenCount} khách đã làm dịch vụ đang ẩn. <button type="button" className="text-[#3e91ab] hover:underline" onClick={() => { setPage(1); setShowAll(true); }}>Hiện tất cả</button></span>
                                                    : !showAll && listTagIds !== null
                                                        ? <span>Không có khách ở các thẻ đang hiện. Khách đã làm dịch vụ đang ẩn, có thể khách cần tìm nằm trong số đó. <button type="button" className="text-[#3e91ab] hover:underline" onClick={() => { setPage(1); setShowAll(true); }}>Gồm khách đã làm DV</button></span>
                                                        : "Không có khách phù hợp bộ lọc"}
                                        />
                                    ),
                                }}
                                pagination={demoMode ? {
                                    current: localPage,
                                    pageSize: 20,
                                    total: rows.length,
                                    showSizeChanger: false,
                                    onChange: (p) => setLocalPage(p),
                                } : {
                                    current: page,
                                    pageSize: 20,
                                    total,
                                    showSizeChanger: false,
                                    onChange: (p) => setPage(p),
                                }}
                                scroll={{ x: scrollX }}
                                sticky={{ offsetHeader: 64 + stickyH, getContainer: scrollBox }}
                                // Thanh cuộn ngang nổi ở đáy màn hình: làm dày, rõ để khỏi phải kéo xuống cuối bảng
                                className="custom-ant-table [&_.ant-table-sticky-scroll]:!h-3 [&_.ant-table-sticky-scroll]:!opacity-100 [&_.ant-table-sticky-scroll-bar]:!h-2 [&_.ant-table-sticky-scroll-bar]:!bg-[#3e91ab]/60 [&_.ant-table-sticky-scroll-bar:hover]:!bg-[#3e91ab]"
                            />
                        </>
                    )}
                </div>

                {selectedRecord && selected && (
                    <LeadDrawer
                        key={`${selected.rec.id}-${selected.seq}`}
                        record={selectedRecord}
                        tags={tags}
                        careStatuses={careStatuses}
                        canApprove={role === "ops"}
                        initial={selected.action}
                        readOnly={readOnly}
                        canNext={!readOnly && rows.length > 1}
                        onNext={openNext}
                        onClose={() => { setSelected(null); setInCall(false); }}
                        onCallChange={setInCall}
                        expanded={drawerWide}
                        onExpandedChange={setDrawerWide}
                    />
                )}
            </div>

            <Modal
                open={!!deleteCustomer}
                onCancel={() => setDeleteCustomer(null)}
                footer={null}
                width={480}
                className="dark:[&_.ant-modal-content]:bg-[#1f1f1f] dark:[&_.ant-modal-header]:bg-[#1f1f1f]"
            >
                <div className="flex gap-4">
                    <ExclamationCircleFilled style={{ fontSize: 24, color: "#faad14" }} />
                    <div>
                        <div className="t-text-white mb-2 text-lg font-semibold">{t('customer.delete_customer')}</div>
                        <div className="mb-6 text-gray-600">{t('customer.confirm_delete')} "{deleteCustomer?.display_name}"?</div>
                        <div className="flex justify-end gap-3">
                            <Button onClick={() => setDeleteCustomer(null)}>{t('customer.cancel')}</Button>
                            <Button danger type="primary" loading={deleteLoading} onClick={handleDeleteCustomer}>{t('customer.delete')}</Button>
                        </div>
                    </div>
                </div>
            </Modal>

            {canManage && (
                <ReassignSettings
                    open={openReassign}
                    onClose={() => setOpenReassign(false)}
                    tags={tags}
                    staffs={teleStaffs.map((x) => ({ id: x.value, name: x.label }))}
                    pageStaffs={pageStaffs}
                    preview={(c: ReassignCfg) => findStale(leadAll, leads, tags, c, now).length}
                    chatPreview={(c: ChatReassignCfg) => findChatStale(allData, leads, c, now).length}
                />
            )}
            {canManage && (
                <ReassignModal
                    open={!!pick}
                    kind={pick ?? "call"}
                    rule={pick === "chat" ? chatRule : ruleText}
                    items={pickItems}
                    staffs={pickStaffs}
                    loads={pickLoads}
                    suggest={pickSuggest}
                    onClose={() => setPick(null)}
                    onSettings={() => setOpenReassign(true)}
                    onConfirm={confirmPick}
                />
            )}

            <CreateCustomerModal
                open={openCreate}
                onClose={() => setOpenCreate(false)}
                onSubmit={async (v) => {
                    setStatusRefreshKey((k) => k + 1);
                    // Tìm khách vừa tạo: id mới xuất hiện sau khi tải lại (hoặc trùng SĐT) → ghim lên đầu
                    const before = new Set(data.map((r) => r.id));
                    const items = await fetchCustomers();
                    const phone = String(v?.phone ?? "").replace(/\D/g, "");
                    let hit = items?.filter((r) => !before.has(r.id)).sort((a, b) => b.id - a.id)[0]
                        ?? items?.find((r) => phone && String(r.phone ?? "").replace(/\D/g, "") === phone);
                    // Máy chủ không trả khách mới ở trang đang xem → tìm theo SĐT rồi chèn lên đầu danh sách
                    if (!hit && phone) {
                        const res = await mixGetV1('/api/v1/be/customers', { search: phone, page: 1 }).catch(() => null);
                        const found = (res?.data?.items ?? []) as ChannelCustomerItem[];
                        // SĐT có thể bị che (090***1099): khớp đuôi, hoặc kết quả tìm duy nhất
                        hit = found.find((r) => String(r.phone ?? "").replace(/\D/g, "").endsWith(phone.slice(-4))) ?? (found.length === 1 ? found[0] : undefined);
                        if (hit) { const h = hit; setData((d) => [h, ...d.filter((x) => x.id !== h.id)]); }
                    }
                    if (!hit) return;
                    setFreshIds((ids) => [hit.id, ...ids.filter((x) => x !== hit.id)]);
                    // Bộ lọc nhanh đang bật có thể che mất khách mới
                    setQuick(null);
                    setLocalPage(1);
                }}
            />
        </Layout>
    );
};

export default CustomerCare;
